import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { X, FileText, ArrowRight, Share2 } from "lucide-react";
import { playPreferSound, rememberMuteChoice } from "@/lib/demoVideoSound";

// A real screen recording of the actual app, re-recorded 2026-09-28 with
// scripts/demo-video/record.py (create a resume with AI -> Job Match ->
// AI Style Studio). Re-run that script when these screens change; the
// same video is also shareable on its own at /demo (/demo.html for social).
const VIDEO_VERSION = "2026-09-28-music";

export const DemoModal = ({ open, onClose, onStart }) => {
  // Starts muted (product decision 2026-09-29); the viewer turns sound on
  // with the video controls.
  const videoRef = useRef(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!open || !v) return;
    playPreferSound(v);
  }, [open]);
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-[80] flex items-center justify-center p-4" data-testid="demo-modal" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0F211B] border border-white/10 w-full max-w-4xl rounded-2xl overflow-hidden shadow-2xl"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div className="flex items-center gap-2 text-white">
            <div className="bg-[#A67C52] text-white p-1.5 rounded-lg"><FileText className="w-4 h-4" /></div>
            <span className="text-xs font-black uppercase tracking-widest">See ResumePilot360 in 45 seconds</span>
          </div>
          <button data-testid="demo-modal-close" onClick={onClose} className="text-white/60 hover:text-white" aria-label="Close demo">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="bg-black">
          <video
            data-testid="demo-video"
            className="w-full h-auto block"
            src={`/videos/app-demo.mp4?v=${VIDEO_VERSION}`}
            poster={`/videos/app-demo-poster.jpg?v=${VIDEO_VERSION}`}
            ref={videoRef}
            onVolumeChange={rememberMuteChoice}
            controls
            loop
            playsInline
          />
        </div>

        <div className="px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-[#A9C2B6] max-w-md">
            Real product screens — create a resume with AI, check your job match, and style it in AI Style Studio.
          </p>
          <div className="flex items-center gap-2">
            <a
              href="/demo"
              data-testid="demo-share-link"
              className="text-xs font-bold text-[#A9C2B6] hover:text-[#A67C52] inline-flex items-center gap-1.5 px-3 py-2"
            >
              <Share2 className="w-3.5 h-3.5" /> Share
            </a>
            <button
              data-testid="demo-start-building"
              onClick={() => { onClose(); onStart(); }}
              className="flex-shrink-0 rounded-full bg-[#A67C52] text-white font-bold px-5 py-2.5 hover:bg-[#8A6640] transition-all text-xs flex items-center gap-2"
            >
              Start Building <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
